import { useCallback, useMemo, useState, type FC } from "react"
import { useTranslation } from "react-i18next"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"

import type { SpanHeatmapPlotView } from "types/dashboard.types"
import type { SpanDatum } from "types/spans.types"

import useSpanFrequencyColors from "helpers/hooks/useSpanFrequencyColors"
import { axisTickIndexes, tickStepFor } from "helpers/utils/spanRibbonWindow"
import {
  DEFAULT_WINDOW,
  getCurrentLimiterMethods,
  getLastLimiterNote,
  getNeutralLane,
  getLanes,
  getOverviewGradient,
  getSpanIndexesById,
  getSpanInfo,
  HANDLE_WIDTH,
  LABEL_WIDTH,
  LANE_TITLE_HEIGHT,
  MARKER_HIT_WIDTH,
  MIN_WINDOW,
  ROW_HEIGHT,
  type LaneMarkers,
} from "./helpers"
import useRibbonWindow from "./useRibbonWindow"
import LaneStrips from "./LaneStrips"
import SummaryRow from "./SummaryRow"
import WindowScrubber from "./WindowScrubber"

// Touch-sized overrides. Omitted, the desktop constants apply.
export interface RibbonWindowConfig {
  // Spans visible at once before the user resizes the window.
  size: number
  // Lane height in px. A bar is also a tap target.
  rowHeight: number
  handleHitSize: number
  // The arrows carry the tooltips, so they need a hit area of their own.
  markerHitSize: number
  // Floor the window can be dragged down to.
  minWindow: number
}

interface RibbonBaseProps {
  view: SpanHeatmapPlotView
  // Never derived here: the card builds it once and hands the same dataset to
  // the map, which is what keeps the two views' colors in agreement.
  spanData: SpanDatum[]
  hoveredSpanId?: number | null
  onHoverSpan?: (spanId: number | null) => void
  // Clicking a cell, which the card uses to fly the map there.
  onSelectSpan?: (spanId: number | null) => void
  // Span pinned by a click, here or on the map. Unlike hover it persists, so
  // the readout survives the pointer leaving, and the window scrolls to it
  // when the selection comes from outside.
  selectedSpanId?: number | null
  windowConfig?: RibbonWindowConfig
  // The readout and totals strip. Off where something else owns those numbers.
  showSummaryRow?: boolean
  // The window the counts cover, preformatted. Sits with the totals: a count
  // with no period attached isn't a figure you can act on.
  dateRangeLabel?: string
}

interface FrequencyRibbonProps extends RibbonBaseProps {
  mode?: "frequency"
  // From the same `buildSpanDataFromCounts` call as `spanData`, so the ribbon
  // and the map scale alike.
  maxPct: number
  // Spans to flag with an arrow above the lane: the most frequent, in that
  // span's color, and the one limiting now, in white. Same spans the map rings.
  markersByKey?: Partial<Record<"aar" | "dlr", LaneMarkers>>
  // Methods whose counts failed. Their lane is hatched rather than drawn as
  // zeros, which would claim the method never limited the line.
  laneErrors?: Partial<Record<"aar" | "dlr", boolean>>
}

/**
 * The line itself: one neutral lane, no figures, no legend. A prop and not a
 * guess from the data — all-zero counts are a real result when a method is down.
 * The frequency inputs are `never` so passing one is an error.
 */
interface NeutralRibbonProps extends RibbonBaseProps {
  mode: "neutral"
  maxPct?: never
  markersByKey?: never
  laneErrors?: never
}

type LimitingSpansHeatmapProps = FrequencyRibbonProps | NeutralRibbonProps

const LimitingSpansHeatmap: FC<LimitingSpansHeatmapProps> = ({
  view,
  mode = "frequency",
  spanData,
  // Neutral mode passes none, and its pcts are all 0 — any non-zero works.
  maxPct = 1,
  markersByKey,
  hoveredSpanId,
  onHoverSpan,
  onSelectSpan,
  selectedSpanId,
  windowConfig,
  showSummaryRow = true,
  laneErrors,
  dateRangeLabel,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"
  const { colorFor, legend, overview, route, hover } = useSpanFrequencyColors()

  const isNeutral = mode === "neutral"
  // Dropping the gutter is the one omission that moves the grid: neutral bars
  // start at the container's left edge.
  const labelWidth = isNeutral ? 0 : LABEL_WIDTH

  const isControlled = onHoverSpan !== undefined
  const isSelectionControlled = selectedSpanId !== undefined

  const rowHeight = windowConfig?.rowHeight ?? ROW_HEIGHT
  const handleHitWidth = windowConfig?.handleHitSize ?? HANDLE_WIDTH
  const markerHitWidth = windowConfig?.markerHitSize ?? MARKER_HIT_WIDTH
  const defaultWindow = windowConfig?.size ?? DEFAULT_WINDOW
  const minWindow = windowConfig?.minWindow ?? MIN_WINDOW

  const rows = useMemo(
    () => (isNeutral ? getNeutralLane(spanData) : getLanes(view, spanData)),
    [isNeutral, view, spanData],
  )

  const total = rows[0]?.points.length ?? 0

  const indexBySpanId = useMemo(() => getSpanIndexesById(rows), [rows])

  const [hoveredSpanIndex, setHoveredSpanIndex] = useState<number | null>(null)
  const [internalSelectedSpanId, setInternalSelectedSpanId] = useState<number | null>(
    null,
  )
  const activeSelectedSpanId = isSelectionControlled
    ? (selectedSpanId ?? null)
    : internalSelectedSpanId

  // Hover wins while it lasts, then the selection carries the readout, so the
  // panel never goes blank after a click.
  const hoveredId = isControlled
    ? (hoveredSpanId ?? null)
    : hoveredSpanIndex != null
      ? (rows[0]?.points[hoveredSpanIndex - 1]?.spanId ?? null)
      : null
  const activeSpanId = hoveredId ?? activeSelectedSpanId
  const activeSpanIndex =
    activeSpanId != null ? (indexBySpanId.get(activeSpanId) ?? null) : hoveredSpanIndex

  const selectedSpanIndex =
    activeSelectedSpanId != null
      ? (indexBySpanId.get(activeSelectedSpanId) ?? null)
      : null

  const {
    start,
    size,
    isFitted,
    showScrubber,
    visibleIndexes,
    dragMode,
    trackRef,
    beginDrag,
    dragHandlers,
    fitAll,
  } = useRibbonWindow({ total, defaultWindow, minWindow, selectedSpanIndex })

  const activeInfo = useMemo(
    () => getSpanInfo(rows, activeSpanIndex, maxPct),
    [rows, activeSpanIndex, maxPct],
  )

  // The white arrow says this too, but only once you have hovered it.
  const lastLimiterNote = useMemo(
    () => getLastLimiterNote(getCurrentLimiterMethods(activeSpanId, markersByKey), t),
    [activeSpanId, markersByKey, t],
  )

  const handleMouseEnter = useCallback(
    (spanIndex: number, spanId: number | null) => {
      setHoveredSpanIndex(spanIndex)
      onHoverSpan?.(spanId)
    },
    [onHoverSpan],
  )

  const handleMouseLeave = useCallback(() => {
    setHoveredSpanIndex(null)
    onHoverSpan?.(null)
  }, [onHoverSpan])

  const handleSelect = useCallback(
    (spanId: number | null) => {
      if (!isSelectionControlled) setInternalSelectedSpanId(spanId)
      onSelectSpan?.(spanId)
    },
    [isSelectionControlled, onSelectSpan],
  )

  // The whole line as one gradient, a stop per span centre, so neighbours blend
  // and the strip reads as heat rather than 250 ticks. Not keyed on the window
  // or the drag: the string is stable, so dragging never rebuilds it.
  const overviewGradient = useMemo(
    () =>
      isNeutral
        ? // A flat bed. The mask, window and handles still mean what they did,
          // which is why the scrubber stays in neutral mode.
          `linear-gradient(to right, ${route}, ${route})`
        : getOverviewGradient(rows, maxPct, colorFor, overview),
    [isNeutral, route, rows, maxPct, colorFor, overview],
  )

  const tickIndexes = useMemo(
    () => new Set(axisTickIndexes(start, size, tickStepFor(size))),
    [start, size],
  )

  if (rows.length === 0 || total === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Typography variant="body2" color="text.secondary">
          {t("generic.NO_DATA_IN_TIME_WINDOW")}
        </Typography>
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        // Deliberately not `overflow: hidden`: clipping turned a few tight pixels
        // into a legend that vanished. Every part has a fixed height instead, so
        // the total is predictable and fits what the card gives it.
        justifyContent: "flex-start",
        minHeight: 0,
        ...(dragMode
          ? {
              userSelect: "none",
              cursor:
                dragMode === "left" || dragMode === "right" ? "ew-resize" : "grabbing",
            }
          : null),
      }}
    >
      {showSummaryRow && !isNeutral && (
        <SummaryRow
          rows={rows}
          labelWidth={labelWidth}
          laneErrors={laneErrors}
          dateRangeLabel={dateRangeLabel}
          activeInfo={activeInfo}
          lastLimiterNote={lastLimiterNote}
          colorFor={colorFor}
          locale={locale}
        />
      )}
      {/* One lane needs no gutter to tell it from another, and dropping it
          gives the strip the full width. The name sits above instead. */}
      {isNeutral && (
        <Box sx={{ height: LANE_TITLE_HEIGHT, display: "flex", alignItems: "center" }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.6rem",
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: "0.05em",
            }}
          >
            {t("spans.ampacity.lane.spans")}
          </Typography>
        </Box>
      )}
      <LaneStrips
        rows={rows}
        visibleIndexes={visibleIndexes}
        indexBySpanId={indexBySpanId}
        isNeutral={isNeutral}
        maxPct={maxPct}
        rowHeight={rowHeight}
        markerHitWidth={markerHitWidth}
        markersByKey={markersByKey}
        laneErrors={laneErrors}
        activeSpanIndex={activeSpanIndex}
        selectedSpanIndex={selectedSpanIndex}
        colorFor={colorFor}
        route={route}
        tickIndexes={tickIndexes}
        onEnter={handleMouseEnter}
        onLeave={handleMouseLeave}
        onSelect={handleSelect}
      />

      {showScrubber && (
        <WindowScrubber
          total={total}
          start={start}
          size={size}
          isFitted={isFitted}
          labelWidth={labelWidth}
          handleHitWidth={handleHitWidth}
          isCompact={Boolean(windowConfig)}
          overviewGradient={overviewGradient}
          frameColor={hover}
          trackRef={trackRef}
          beginDrag={beginDrag}
          dragHandlers={dragHandlers}
          onFitAll={fitAll}
        />
      )}
      {/* Nothing to key: neutral prints no frequency. */}
      {!isNeutral && (
        <Box
          sx={{
            alignSelf: "center",
            mt: 2.5,
            flexShrink: 0,
            width: 140,
          }}
        >
          {/* Straight off the palette, so the legend can't drift from the ramp. */}
          <Box sx={{ display: "flex", height: 8, borderRadius: 0.5, overflow: "hidden" }}>
            {legend.map((color, i) => (
              <Box
                key={color}
                sx={{
                  flex: 1,
                  bgcolor: color,
                  borderRadius:
                    i === 0 ? "4px 0 0 4px" : i === legend.length - 1 ? "0 4px 4px 0" : 0,
                }}
              />
            ))}
          </Box>
          <Stack direction="row" justifyContent="space-between" mt={0.25}>
            <Typography sx={{ fontSize: "0.55rem", color: "text.disabled" }}>
              {t("spans.ampacity.legend.lessFrequent")}
            </Typography>
            <Typography sx={{ fontSize: "0.55rem", color: "text.disabled" }}>
              {t("spans.ampacity.legend.moreFrequent")}
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export default LimitingSpansHeatmap
